import { authorizeUrl, codeChallengeS256, decodeClaims, logoutUrl, parseCallback, randomUrlSafe, type OidcConfig } from "./pkce.js";

/**
 * Browser session management for Cognito Hosted UI (Authorization Code +
 * PKCE). No custom password handling; no client secret; tokens never appear in
 * the URL after the callback and are never logged.
 *
 *   signIn()        → store {state, verifier, returnTo} → redirect to /oauth2/authorize
 *   completeSignIn()→ verify state → exchange code + verifier → store tokens
 *                     → strip ?code&state from the address bar
 *   currentToken()  → access token, refreshed via the refresh token when expired
 *   signOut()       → clear storage → Cognito /logout
 *
 * Storage is tab-scoped (sessionStorage), so a session survives reloads in the
 * same tab but not new tabs/browser restarts. Dependencies are injected so the
 * flow is unit-testable without a browser.
 */

export interface KeyValueStore {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
  removeItem(k: string): void;
}

export interface AuthEnv {
  readonly config: OidcConfig;
  readonly storage: KeyValueStore;
  readonly fetch: typeof fetch;
  readonly crypto: Crypto;
  readonly now: () => number;
  /** Navigate the browser (full-page redirect). */
  readonly redirect: (url: string) => void;
  /** Replace the current URL without the OAuth query parameters. */
  readonly replaceUrl: (url: string) => void;
}

export interface StoredTokens {
  readonly accessToken: string;
  readonly idToken: string | null;
  readonly refreshToken: string | null;
  /** Epoch ms. */
  readonly expiresAt: number;
  readonly sub: string;
  readonly email: string | null;
}

const PENDING = "corefinops.auth.pending";
export const TOKENS_KEY = "corefinops.auth.tokens";
/** Refresh slightly before expiry to avoid racing the API. */
export const SKEW_MS = 60_000;

/**
 * The session surface AuthGate needs regardless of how tokens were obtained
 * (OAuth redirect or the in-house password flow). Both AuthManager and
 * CognitoPasswordAuth satisfy this structurally.
 */
export interface TokenSource {
  /** A valid access token, refreshing when near expiry; null = must sign in again. */
  currentToken(): Promise<string | null>;
  /** The current stored session (without a network call), or null. */
  session(): StoredTokens | null;
  /** Forget the stored session. */
  clear(): void;
}

export class AuthError extends Error {
  constructor(readonly code: "state_mismatch" | "missing_pending" | "provider_error" | "token_exchange_failed" | "invalid_token", message: string) {
    super(message);
    this.name = "AuthError";
  }
}

export class AuthManager {
  constructor(private readonly env: AuthEnv) {}

  async signIn(returnTo: string): Promise<void> {
    const state = randomUrlSafe(32, this.env.crypto);
    const verifier = randomUrlSafe(48, this.env.crypto);
    const challenge = await codeChallengeS256(verifier, this.env.crypto);
    this.env.storage.setItem(PENDING, JSON.stringify({ state, verifier, returnTo: safeReturnTo(returnTo), at: this.env.now() }));
    this.env.redirect(authorizeUrl(this.env.config, { state, codeChallenge: challenge }));
  }

  /** Handle the redirect back. Returns where to send the user next. */
  async completeSignIn(location: { search: string; origin: string; pathname: string }): Promise<string> {
    const params = parseCallback(location.search);
    const pendingRaw = this.env.storage.getItem(PENDING);
    this.env.storage.removeItem(PENDING); // one attempt per pending sign-in
    // Remove ?code / ?state / ?error from the address bar immediately.
    this.env.replaceUrl(`${location.origin}/`);
    if (!params) throw new AuthError("missing_pending", "No sign-in response was found.");
    if (params.kind === "error") throw new AuthError("provider_error", params.description ?? params.error);
    if (!pendingRaw) throw new AuthError("missing_pending", "This sign-in response was not started from this browser tab. Please sign in again.");
    const pending = JSON.parse(pendingRaw) as { state: string; verifier: string; returnTo: string; at: number };
    if (pending.state !== params.state) throw new AuthError("state_mismatch", "The sign-in response did not match this session (state mismatch). Please sign in again.");
    if (this.env.now() - pending.at > 10 * 60_000) throw new AuthError("missing_pending", "The sign-in attempt expired. Please sign in again.");

    const body = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: this.env.config.clientId,
      code: params.code,
      redirect_uri: this.env.config.redirectUri,
      code_verifier: pending.verifier,
    });
    this.store(await this.tokenRequest(body, null));
    return pending.returnTo;
  }

  /** The current session, or null when signed out/expired (without network). */
  session(): StoredTokens | null {
    const raw = this.env.storage.getItem(TOKENS_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as StoredTokens;
    } catch {
      this.env.storage.removeItem(TOKENS_KEY);
      return null;
    }
  }

  /** A valid access token, refreshing when near expiry. Null = must sign in again. */
  async currentToken(): Promise<string | null> {
    const s = this.session();
    if (!s) return null;
    if (s.expiresAt - SKEW_MS > this.env.now()) return s.accessToken;
    if (!s.refreshToken) {
      this.clear();
      return null;
    }
    try {
      const body = new URLSearchParams({ grant_type: "refresh_token", client_id: this.env.config.clientId, refresh_token: s.refreshToken });
      const next = await this.tokenRequest(body, s.refreshToken);
      this.store(next);
      return next.accessToken;
    } catch {
      this.clear();
      return null;
    }
  }

  clear(): void {
    this.env.storage.removeItem(TOKENS_KEY);
    this.env.storage.removeItem(PENDING);
  }

  signOut(): void {
    this.clear();
    this.env.redirect(logoutUrl(this.env.config));
  }

  private store(t: StoredTokens): void {
    this.env.storage.setItem(TOKENS_KEY, JSON.stringify(t));
  }

  private async tokenRequest(body: URLSearchParams, priorRefresh: string | null): Promise<StoredTokens> {
    const res = await this.env.fetch(this.env.config.tokenUrl, {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
    if (!res.ok) throw new AuthError("token_exchange_failed", `Sign-in could not be completed (${res.status}).`);
    const j = (await res.json()) as { access_token?: string; id_token?: string; refresh_token?: string; expires_in?: number };
    if (!j.access_token) throw new AuthError("token_exchange_failed", "The identity provider returned no access token.");
    const claims = decodeClaims(j.access_token);
    const idClaims = j.id_token ? decodeClaims(j.id_token) : null;
    const sub = (claims?.["sub"] ?? idClaims?.["sub"]) as string | undefined;
    if (!sub) throw new AuthError("invalid_token", "The access token has no subject.");
    const exp = typeof claims?.["exp"] === "number" ? (claims["exp"] as number) * 1000 : this.env.now() + (j.expires_in ?? 3600) * 1000;
    return {
      accessToken: j.access_token,
      idToken: j.id_token ?? null,
      refreshToken: j.refresh_token ?? priorRefresh,
      expiresAt: exp,
      sub,
      email: (idClaims?.["email"] as string | undefined) ?? null,
    };
  }
}

/** Only in-app hash routes are allowed as post-sign-in destinations (no open redirect). */
export function safeReturnTo(v: string): string {
  return /^#\/[\w\-/:%.?=&]*$/.test(v) ? v : "#/overview";
}
